import { useEffect, useRef, useState } from "react";
import { Alert } from "react-bootstrap";
import { useParams } from "react-router-dom";
import ChatGateway from "../../api/gateways/ChatGateway";
import { PageLayout } from "../../components/page-layout";
import "./ChatBot.css";
import { ChatInput } from "./ChatInput";
import { ChatMessageList } from "./ChatMessageList";
import { ChatSidebar } from "./ChatSidebar";
import { ChatSignatureComparisonReport } from "./ChatSignatureComparisonReport";
import { ChatTableReport } from "./ChatTableReport";
import { useChatBot } from "./useChatBot";

export function ChatBotScreen() {
  const { sessionId: sessionIdParam, reportId } = useParams<{
    sessionId?: string;
    reportId?: string;
  }>();
  const [reportType, setReportType] = useState<string | null>(null);
  const refreshSessionsRef = useRef<(() => Promise<void>) | null>(null);

  const {
    messages,
    isTyping,
    error,
    isConnected,
    sendMessage,
    clearConversation,
    progressMessage,
    progressPercent,
    exportId,
    cancelExportJob,
    loadSession,
    sessionId,
    chatUi,
    elapsedSeconds,
    uploadFraudControlCsv,
    isFraudControlUploading,
  } = useChatBot(sessionIdParam);

  // Detect report type when reportId is present
  useEffect(() => {
    if (reportId) {
      // Try to fetch report metadata to determine type
      // For now, we'll use a simple heuristic: try to fetch as signature comparison first
      ChatGateway.api
        .getSignatureComparisonReport(reportId)
        .then((response) => {
          // If response starts with "# Digital vs Physical", it's a signature comparison
          if (
            typeof response === "string" &&
            response.includes("# Digital vs Physical")
          ) {
            setReportType("signature-comparison");
          } else {
            setReportType("table");
          }
        })
        .catch(() => {
          // If it fails, assume it's a table report
          setReportType("table");
        });
    } else {
      setReportType(null);
    }
  }, [reportId]);

  // Refresh sidebar when sessionId changes (new session created or session loaded)
  useEffect(() => {
    if (sessionId && refreshSessionsRef.current) {
      refreshSessionsRef.current();
    }
  }, [sessionId]);

  // Refresh sidebar when assistant finishes responding (isTyping changes from true to false)
  const prevIsTypingRef = useRef(isTyping);
  useEffect(() => {
    // If typing just finished (was true, now false), refresh sidebar
    if (
      prevIsTypingRef.current === true &&
      isTyping === false &&
      refreshSessionsRef.current
    ) {
      // Small delay to ensure backend has updated the session
      const timeoutId = setTimeout(() => {
        if (refreshSessionsRef.current) {
          refreshSessionsRef.current();
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }
    prevIsTypingRef.current = isTyping;
  }, [isTyping]);

  return (
    <PageLayout title="Chat Assistant" className="d-flex gap-3">
      <ChatSidebar
        onSessionClick={loadSession}
        currentSessionId={sessionId}
        onRefreshReady={(refreshFn) => {
          refreshSessionsRef.current = refreshFn;
        }}
        onClearConversation={clearConversation}
        hasMessages={messages.length > 0}
      />
      <div className="chat-container">
        {!isConnected && (
          <div className="connection-status disconnected">
            ⚠️ Connecting to chat service...
          </div>
        )}

        {error && (
          <Alert variant="danger" className="m-3" dismissible>
            {error}
          </Alert>
        )}

        {!reportId && (
          <ChatMessageList
            messages={messages}
            isTyping={isTyping}
            progressMessage={progressMessage}
            progressPercent={progressPercent}
            exportId={exportId}
            cancelExportJob={cancelExportJob}
            ui={chatUi}
            elapsedSeconds={elapsedSeconds}
          />
        )}
        {reportId && reportType === "table" && (
          <ChatTableReport reportId={reportId} />
        )}
        {reportId && reportType === "signature-comparison" && (
          <ChatSignatureComparisonReport reportId={reportId} />
        )}

        {!reportId && (
          <div className="d-flex gap-2 px-3 pb-2">
            <small className="text-muted ms-auto mt-2">
              {chatUi.messagesCount(messages.length)}
            </small>
          </div>
        )}

        {!reportId && (
          <ChatInput
            onSendMessage={sendMessage}
            onUploadCsv={uploadFraudControlCsv}
            disabled={!isConnected || isTyping}
            uploadDisabled={isFraudControlUploading}
            placeholder={chatUi.placeholder}
            sendLabel={chatUi.send}
            uploadButtonTitle={chatUi.fraudControlUploadButton}
            fraudControlModal={{
              title: chatUi.fraudControlModalTitle,
              description: chatUi.fraudControlModalDescription,
              upload: chatUi.fraudControlModalUpload,
              close: chatUi.fraudControlModalClose,
            }}
          />
        )}
      </div>
    </PageLayout>
  );
}
